// @ts-check

/**
 * @file Renders the project auto-assigned users route.
 */

import {useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../../src/components/button"
import Card from "../../../src/components/card"
import errorMessage from "../../../src/shared/error-message"
import {Project} from "../../../src/frontend-models/project"
import {ProjectAutoassignedUser} from "../../../src/frontend-models/project-autoassigned-user"
import ProjectUserAssignmentRow from "../../../src/screens/projects/project-user-assignment-row"
import {requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import UsersSelect from "../../../src/components/users/select"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} ProjectAutoassignedUsersScreenProps */
/** @typedef {{id: string, email: string, name: string}} ProjectAutoassignedUserOption */
/** @typedef {{id: string, user: ProjectAutoassignedUserOption}} ProjectAutoassignedUserRow */

/**
 * @typedef {object} ProjectAutoassignedUsersScreenState
 * @property {string[]} assignedUserIds User ids already assigned to the project.
 * @property {boolean} loading Whether assignments are loading.
 * @property {string} projectName Loaded project name.
 * @property {ProjectAutoassignedUserRow[]} rows Current assignment rows.
 * @property {string} selectedUserId User selected for assignment.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Auto-assigned users manager for a single project.
 * @augments {ShapeComponent<ProjectAutoassignedUsersScreenProps, ProjectAutoassignedUsersScreenState>}
 */
class ProjectAutoassignedUsersScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  projectID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {ProjectAutoassignedUsersScreenState} */
  state = {
    assignedUserIds: [],
    loading: true,
    projectName: "",
    rows: [],
    selectedUserId: "",
    status: null
  }
  loadRequestId = 0

  /** Initializes route params and assignment loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const projectID = requiredRouteParam(params, "id", "Project ID")
    this.params = params
    this.projectID = projectID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadData()
    }, [projectID])
  }

  /**
   * Renders the auto-assigned users manager.
   * @returns {ReactNode} Auto-assigned users screen content.
   */
  render() {
    const {
      assignedUserIds,
      loading,
      projectName,
      rows,
      selectedUserId,
      status
    } = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="projectAutoassignedUsersScreen"
        />
      )
    }
    const screenTitle = _("Auto-assigned users for %{projectName}", {
      projectName
    })
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectAutoassignedUsersScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <Text
          style={styles.title ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="projectAutoassignedUsersScreen/title"
        >
          {screenTitle}
        </Text>
        {status &&
          <Text
            style={styles[`status-${themeName}-${status.kind}`] ||= {
              color: status.kind === "error" ? colors.dangerText : colors.successText,
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="projectAutoassignedUsersScreen/status"
          >
            {status.text}
          </Text>
        }
        <Card
          style={styles.addRow ||= {
            alignItems: "center",
            flexDirection: "row",
            gap: 8,
            marginBottom: 16
          }}
          testID="projectAutoassignedUsersScreen/addRow"
        >
          <View
            style={styles.selectWrapper ||= {
              flex: 1
            }}
            testID="projectAutoassignedUsersScreen/addRow/userSelect"
          >
            <UsersSelect
              excludeValues={assignedUserIds}
              includeEmailInLabel
              onChangeValue={this.tt.onUserSelectChange}
              placeholder={_("Select user")}
              testID="projectAutoassignedUsersScreen/addRow/userSelect/select"
              values={selectedUserId ? [selectedUserId] : []}
            />
          </View>
          <Button
            disabled={!selectedUserId}
            iconName="plus"
            label={_("Add")}
            onPress={this.tt.onAddPress}
            testID="projectAutoassignedUsersScreen/addRow/addButton"
            variant="neutral"
          />
        </Card>
        {rows.length === 0 &&
          <Text
            style={styles[`emptyText-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 15
            }}
            testID="projectAutoassignedUsersScreen/emptyText"
          >
            {_("No auto-assigned users yet")}
          </Text>
        }
        {rows.map((row) =>
          <ProjectUserAssignmentRow
            key={row.id}
            onRemovePress={this.tt.onRemovePress}
            row={row}
            testIDPrefix="projectAutoassignedUsersScreen"
          />
        )}
      </View>
    )
  }

  /**
   * Loads project assignments and available users.
   * @returns {Promise<void>} Resolves when assignment state updates.
   */
  loadData = async () => {
    const requestId = ++this.loadRequestId
    const {projectID} = this.tt
    try {
      const project = await Project
        .where({
          id: projectID
        })
        .preload({
          projectAutoassignedUsers: {user: true}
        })
        .first()
      if (this.loadRequestId !== requestId) return
      const assignments = project ? project.projectAutoassignedUsersLoaded() : []
      const assignedUserIds = assignments.map((assignment) =>
        String(assignment.userId())
      )
      this.s.projectName = project ? project.name() || "" : ""
      this.s.rows = assignments.map((assignment) => {
        const user = assignment.user()
        return {
          id: String(assignment.id()),
          user: {
            email: user ? user.email() || "" : "",
            id: String(assignment.userId() ?? ""),
            name: user
              ? `${user.firstName() || ""} ${user.lastName() || ""}`.trim()
              : ""
          }
        }
      })
      this.s.assignedUserIds = assignedUserIds
      this.s.selectedUserId = ""
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load auto-assigned users")}: ${errorMessage(error)}`
      }
    }
  }

  /**
   * Applies the selected user from the shared user select.
   * @param {(string | number)[] | string | number | undefined} selected Selected value.
   * @returns {void} Nothing.
   */
  onUserSelectChange = (selected) => {
    const selectedUserId = Array.isArray(selected)
      ? selected[0]
        ? String(selected[0])
        : ""
      : selected
        ? String(selected)
        : ""
    this.s.selectedUserId = selectedUserId
  }

  /**
   * Adds an auto-assignment for the selected user.
   * @returns {Promise<void>} Resolves when add handling completes.
   * @rejects {unknown} When saving fails.
   */
  onAddPress = async () => {
    const {projectID} = this.tt
    const {selectedUserId} = this.s
    if (!selectedUserId) {
      throw new Error("Expected a selected user before adding auto-assignment")
    }
    const assignment = new ProjectAutoassignedUser()
    assignment.setProjectId(projectID)
    assignment.setUserId(selectedUserId)
    try {
      await assignment.save()
      this.s.status = {
        kind: "success",
        text: _("User added to auto-assignment")
      }
      await this.loadData()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Removes an auto-assignment.
   * @param {string} assignmentId Assignment id to remove.
   * @returns {Promise<void>} Resolves when remove handling completes.
   * @rejects {unknown} When removal fails.
   */
  onRemovePress = async (assignmentId) => {
    try {
      const assignment = new ProjectAutoassignedUser()
      assignment.setId(assignmentId)
      await assignment.destroy()
      this.s.status = {
        kind: "success",
        text: _("User removed from auto-assignment")
      }
      await this.loadData()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<ProjectAutoassignedUsersScreenProps>} */
const ProjectAutoassignedUsersScreenComponent = memo(shapeComponent(ProjectAutoassignedUsersScreen))

export default ProjectAutoassignedUsersScreenComponent
